// =============================================================================
// RailNavigation.js — LE rail de la coquille (bureau, iPad en paysage)
// (refonte « Mosaïque vivante », lot L6, 28/09/2026 ; synthèse §6.1, §6.2,
// règle couleur v2 point 8, moment M3).
//
// Ce qu'il porte : « Ma semaine », puis, pour qui en a un, « Mon espace
// salarié » (espace PERSONNEL de la forme d'un module, 26/10/2026 :
// buildNavEspaceSalarie, pas un module métier), puis les modules (neuf, dix pour les
// administrateurs, depuis qu'Affaires publiques est un module, 12/10/2026),
// puis, après un filet, « Administration » et « Super
// administration » pour qui y a accès (29/09/2026, buildNavAdmin : pas des
// modules métier, teinte neutre), puis « Moi » en pied. Chaque module qui a
// des pages porte son sommaire (ses pages, et les pages de ses pages) JUSTE
// SOUS SA LIGNE, en ACCORDÉON (demande de Pierre du 29/09/2026 : ouvert tout
// en bas, sous la liste des modules, il n'était pas là où on le cherchait) :
//   - un seul sommaire ouvert : celui du module de la page, par défaut ;
//     cliquer un AUTRE module ouvre son accueil, donc son sommaire, et replie
//     le précédent ;
//   - le chevron de la ligne replie ou rouvre le sommaire SANS changer de
//     page ; le repli du module courant est mémorisé pour la session
//     (sessionStorage) et levé dès qu'on revient dans ce module depuis
//     ailleurs (rail, palette, lien) : on y trouve toujours sa page ;
//   - le rail défile ; à chaque navigation, la ligne de la page courante est
//     ramenée à l'écran (au plus près, sans animation).
// C'est le « sommaire à deux niveaux » qui remplace à la fois les boutons de
// module de l'en-tête, leurs panneaux survolés et l'ancienne barre latérale.
//
// SOURCE UNIQUE (CLAUDE.md, « Menu desktop = menu mobile ») : AUCUNE entrée
// n'est écrite ici. Les modules arrivent de buildNavModules() (via App.jsx,
// même contexte d'accès), la porte de chaque module de PORTAILS_MODULES, les
// entrées de entreesModule() — tout dans src/navConstants.js.
//
// Couleur (règle v2, point 8) : au repos, glyphe NU à l'encre du module ; au
// survol, lavis de la teinte à 8 % (16 % la nuit) ; à l'actif, tuile PLEINE et
// pastille de fond à 12 %. Le sommaire marque sa page par un filet de 2 px à la
// teinte de l'entrée (sous-teinte d'instance pour AG, CA, CODIR, Bureau).
//
// Mouvement (régime spatial) : la pastille et le filet GLISSENT d'une entrée à
// l'autre (FLIP, --spring-smooth, 380 ms) ; le sommaire se déplie et se replie
// en grid-template-rows 220 ms, le chevron tourne en 180 ms
// (coquilleStyles.js). Rien au clavier (data-instantane), rien sous mouvement
// réduit (la pastille se pose, le sommaire s'ouvre d'un coup).
// =============================================================================
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CadenasAdmin } from "./cadenasAdmin";
import { NAV_COLORS, navTexte, encreSur, teinteEntree, routePortail, entreesModule, libelleEntree, pageServie, compteEntree, compteModule } from "./navConstants";
import { Icone, Tuile } from "./icones";
import { mouvementReduit, saisieClavierRecente } from "./mouvement";
import { optsCoquille, glisserDepuis, rectHomologue, clicAuClavier } from "./navigationCoquille";
import { usePrechargementIntention } from "./prechargementVues";
import PastilleCompteur from "./PastilleCompteur";
import { echelleTexte } from "./echelleTexte";

const NAVY = NAV_COLORS.pilotage;

// Variables de teinte d'une ligne : --cq-h (teinte), --cq-hi (encre de texte),
// --cq-oh (glyphe sur tuile pleine). Toutes DÉRIVÉES de NAV_COLORS.
const teinte = (c) => ({ "--cq-h": c, "--cq-hi": navTexte(c), "--cq-oh": encreSur(c) });

// Modules repliés à la main (chevron), pour la session de l'onglet : { r:
// clés repliées, m: dernier module vu par le rail }. Un rail qui renaît (au
// rechargement, ou après une fiche ouverte sur l'iPad) garde le repli s'il
// renaît dans le MÊME module, le lève s'il arrive d'ailleurs.
export const CLE_REPLIS = "aramis_rail_replis";
const sans = (liste, cle) => liste.filter((k) => k !== cle);
const lireReplis = (actif) => {
  try {
    const v = JSON.parse(sessionStorage.getItem(CLE_REPLIS) || "{}");
    const r = Array.isArray(v.r) ? v.r.filter((k) => typeof k === "string") : [];
    return v.m === actif ? r : sans(r, actif);
  } catch { return []; }
};
const instantane = (e) => clicAuClavier(e) || saisieClavierRecente() || mouvementReduit();

/** Défilement (px, signé) qui rend `cible` visible dans `boite`, au plus près
 *  (comme scrollIntoView block:"nearest", mais sur le seul rail) ; 0 si elle
 *  l'est déjà. Pur, testé. */
export function decalageVisible(boite, cible, marge = 8) {
  const haut = boite.top + marge, bas = boite.bottom - marge;
  if (cible.top < haut) return cible.top - haut;
  if (cible.bottom > bas) return Math.min(cible.bottom - bas, cible.top - haut);
  return 0;
}

function Sommaire({ id, module, entrees, view, ouvert, onNav, intention }) {
  const liste = useRef(null);
  const precedente = useRef(view);
  // Parents dépliés : celui de la page courante d'office, les autres à la main.
  const parentDe = (r) => (entrees.find((e) => e.route === r) || {}).parent || null;
  const [deplies, setDeplies] = useState(() => new Set([parentDe(view), view].filter(Boolean)));
  // Rejoué aussi quand les ENTRÉES de la page arrivent après le montage : les
  // pages de l'audit et du contrôle interne n'entrent au menu qu'une fois le
  // drapeau du serveur lu (src/audit/drapeauAudit.js) ; sans cela, une page
  // ouverte directement (rechargement, lien) laissait son parent replié, et
  // ses voisines derrière un chevron (12/10/2026, rangées de liens retirées
  // au profit du rail).
  const parentCourant = parentDe(view);
  const aDesEnfants = entrees.some((e) => e.parent === view);
  useLayoutEffect(() => {
    const p = parentCourant;
    setDeplies((s) => {
      const n = new Set(s);
      if (p) n.add(p);
      if (aDesEnfants) n.add(view);
      return n.size === s.size ? s : n;
    });
    // Le filet glisse de l'ancienne page à la nouvelle, dans le même sommaire.
    const avant = precedente.current;
    precedente.current = view;
    if (!ouvert || avant === view || mouvementReduit() || saisieClavierRecente() || !liste.current) return;
    const depuis = liste.current.querySelector(`[data-route="${avant}"]`);
    const filet = liste.current.querySelector('[aria-current="page"] > .cq-filet');
    if (depuis && filet) glisserDepuis(filet, rectHomologue(depuis, filet));
  }, [view, parentCourant, aDesEnfants]); // eslint-disable-line react-hooks/exhaustive-deps

  let groupe = null;
  const lignes = [];
  entrees.forEach((e) => {
    if (e.niveau === 2 && !deplies.has(e.parent)) return;
    if (e.niveau === 1 && e.group && e.group !== groupe) {
      groupe = e.group;
      lignes.push(<p key={"g-" + e.group} className="cq-sommaire-groupe">{e.group}</p>);
    }
    const actif = e.route === view;
    const c = teinteEntree(e, module.color) || module.color;
    const enfants = e.niveau === 1 ? entrees.filter((x) => x.parent === e.route).length : 0;
    const deplie = deplies.has(e.route);
    lignes.push(
      <div key={e.route} className="cq-sommaire-ligne" data-niveau={e.niveau}>
        <button type="button" className="cq-sommaire-item" data-route={e.route} style={teinte(c)}
          aria-current={actif ? "page" : undefined} title={e.description ? e.label + " : " + e.description : undefined}
          onClick={(ev) => onNav(e.route, null, optsCoquille(ev))} {...intention(e.route)}>
          {actif && <span className="cq-filet" aria-hidden="true" />}
          <Tuile couleur={c} taille={20} icone={e.icon || "chevron-right"} variante="nue" />
          <span className="cq-sommaire-texte">{libelleEntree(e)}{e.reserveAdmin && <CadenasAdmin />}</span>
          <PastilleCompteur compte={compteEntree(e)} />
        </button>
        {enfants > 0 && (
          <button type="button" className="cq-sommaire-deplier" aria-expanded={deplie}
            aria-label={(deplie ? "Replier" : "Déplier") + " les pages de « " + e.label + " » (" + enfants + ")"}
            onClick={() => setDeplies((s) => { const n = new Set(s); if (n.has(e.route)) n.delete(e.route); else n.add(e.route); return n; })}>
            <Icone name="chevron-right" size={14} />
          </button>
        )}
      </div>
    );
  });

  return (
    <div id={id} className="cq-sommaire" data-ouvert={ouvert ? "1" : "0"} {...(ouvert ? {} : { inert: "" })}
      style={teinte(module.color || NAVY)}>
      <div className="cq-sommaire-in">
        <div className="cq-sommaire-liste" ref={liste} role="group" aria-label={"Sommaire : " + module.label}>
          {lignes}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object[]} modules      buildNavModules(…) (App.jsx, contexte d'accès)
 * @param {object[]} admin        buildNavAdmin(…) : rangés après les modules
 * @param {object[]} personnel    buildNavEspaceSalarie(…) : sous « Ma semaine »
 * @param {string}   view         vue courante
 * @param {object|null} moduleActif module de la vue (moduleForView), ou null
 * @param {Function} onNav        navTo(route, params, opts)
 * @param {Function} onMoi        ouvre « Moi »
 * @param {object}   session      session Aramis (initiales, nom, mutuelle)
 * @param {boolean}  etroit       bureau de moins de 1 024 px : icônes seules
 */
export default function RailNavigation({ modules = [], admin = [], personnel = [], view, moduleActif = null, onNav, onMoi, session, etroit = false }) {
  const rail = useRef(null);
  const liste = useRef(null);
  const intention = usePrechargementIntention();
  const actif = (moduleActif && moduleActif.key) || null;
  const cleActive = view === "ma-semaine" ? "ma-semaine" : actif;
  const precedente = useRef(cleActive);

  // Accordéon : UN sommaire ouvert, celui du module de la page sauf repli à la
  // main. `apercu` : module ouvert au chevron hors de la page (null : tout
  // replié), oublié à la navigation suivante. `sec` : le dernier geste vient
  // du clavier (ou mouvement réduit), rien ne s'anime.
  const [replis, setReplis] = useState(() => lireReplis(actif));
  const [apercu, setApercu] = useState();
  const [sec, setSec] = useState(false);
  const [suivi, setSuivi] = useState({ view, actif });
  if (suivi.view !== view || suivi.actif !== actif) {
    // Navigation (état dérivé, même rendu : aucune image intermédiaire).
    setSuivi({ view, actif });
    setApercu(undefined);
    if (actif && actif !== suivi.actif && replis.includes(actif)) setReplis(sans(replis, actif));
    setSec(instantane());
  }
  const ouvert = apercu !== undefined ? apercu : actif && !replis.includes(actif) ? actif : null;
  useEffect(() => {
    try { sessionStorage.setItem(CLE_REPLIS, JSON.stringify({ r: replis, m: actif })); } catch { /* stockage refusé */ }
  }, [replis, actif]);

  const basculer = (cle, e) => {
    setSec(instantane(e));
    if (cle !== actif) return setApercu(ouvert === cle ? null : cle);
    setApercu(undefined);
    setReplis(ouvert === cle ? [...sans(replis, cle), cle] : sans(replis, cle));
  };

  // À chaque navigation, la page courante (ou le module, sommaire replié) est
  // ramenée dans le rail, sans animation ; après le dépliage s'il y en a un.
  const rendu = useRef({ view: null, ouvert });
  useLayoutEffect(() => {
    const avant = rendu.current;
    rendu.current = { view, ouvert };
    if (avant.view === view) return undefined;
    const montrer = () => {
      const r = rail.current;
      const cible = r && (r.querySelector('[data-ouvert="1"] [aria-current="page"]') || r.querySelector(".cq-rail-item[aria-current]"));
      if (cible) r.scrollTop += decalageVisible(r.getBoundingClientRect(), cible.getBoundingClientRect()) / echelleTexte();
    };
    // Même sommaire : la ligne cible peut n'exister qu'au rendu SUIVANT (un
    // sous-groupe que le Sommaire déplie de lui-même) : on attend deux images,
    // puis on vérifie encore après un éventuel dépliage animé (220 ms).
    if (sec || avant.ouvert === ouvert) {
      let f2 = 0;
      const f1 = requestAnimationFrame(() => { f2 = requestAnimationFrame(montrer); });
      const t2 = sec ? 0 : setTimeout(montrer, 240);
      return () => { cancelAnimationFrame(f1); cancelAnimationFrame(f2); clearTimeout(t2); };
    }
    const t = setTimeout(montrer, 240);
    return () => clearTimeout(t);
  }, [view, ouvert, sec]);

  // La pastille glisse de l'ancienne entrée active à la nouvelle (FLIP). Mesure
  // prise AVANT que les sommaires ne bougent (début de leur transition) : la
  // pastille, logée dans la nouvelle entrée, suit ensuite celle-ci si un
  // sommaire se replie au-dessus.
  useLayoutEffect(() => {
    const avant = precedente.current;
    precedente.current = cleActive;
    if (!avant || !cleActive || avant === cleActive || !liste.current) return;
    if (mouvementReduit() || saisieClavierRecente()) return;
    const depuis = liste.current.querySelector(`[data-cle="${avant}"]`);
    const fond = liste.current.querySelector(`[data-cle="${cleActive}"] > .cq-fond`);
    if (depuis && fond) glisserDepuis(fond, rectHomologue(depuis, fond));
  }, [cleActive]);

  // Rail étroit sous la souris : la bulle (::after) sort à droite du rail, qui
  // ne défile donc pas (overflow:visible, coquilleStyles.js). Quand ses lignes
  // ne tiennent plus en hauteur (dix modules depuis le 12/10/2026, en « Grand »
  // ou « Très grand », fenêtre basse), il DÉFILE (data-deborde) : Administration
  // et Moi restent atteignables ; la bulle cède alors la place à l'infobulle du
  // navigateur (title). Mesuré au montage et à chaque changement de taille.
  const [deborde, setDeborde] = useState(false);
  useLayoutEffect(() => {
    const r = rail.current;
    if (!etroit || !r) { setDeborde(false); return undefined; }
    const mesurer = () => setDeborde(r.scrollHeight > r.clientHeight + 1);
    mesurer();
    window.addEventListener("resize", mesurer);
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(mesurer) : null;
    if (ro) { ro.observe(r); if (liste.current) ro.observe(liste.current); }
    return () => { window.removeEventListener("resize", mesurer); if (ro) ro.disconnect(); };
  }, [etroit]);
  const infobulle = (libelle) => (etroit && deborde ? libelle : undefined);

  const initiales = ((session && session.prenom) || "").charAt(0) + ((session && session.nom) || "").charAt(0);
  const vis = (l) => l.filter((m) => m && m.show !== false);
  const administration = vis(admin);

  const item = (cle, libelle, icone, couleur, route, id, compte = null, reserve = false) => {
    const estActif = cle === cleActive;
    return (
      <button type="button" key={cle} id={id} data-cle={cle} data-tour-id={id}
        className="cq-rail-item" style={teinte(couleur)}
        aria-current={estActif ? (cle === "ma-semaine" ? "page" : "true") : undefined}
        data-bulle={libelle} title={infobulle(libelle)} aria-label={etroit ? libelle + (compte ? " : " + compte.libelle : "") : undefined}
        onClick={(e) => {
          // Le lien du module courant rouvre toujours son sommaire.
          if (cle === actif && ouvert !== cle) basculer(cle, e);
          const tuile = e.currentTarget.querySelector(".cq-rail-tuile");
          onNav(route, null, optsCoquille(e, { tuile, cle }));
        }} {...intention(route)}>
        {estActif && <span className="cq-fond" aria-hidden="true" />}
        <span className="cq-rail-tuile" data-vt-rail={cle}>
          <Tuile couleur={couleur} taille={28} icone={icone} variante={estActif ? "pleine" : "nue"} />
        </span>
        <span className="cq-rail-texte">{libelle}{reserve && <CadenasAdmin />}</span>
        <PastilleCompteur compte={compte} />
      </button>
    );
  };

  // Un module (ou Administration) : sa ligne, puis son sommaire juste dessous.
  const ligne = (m) => {
    const couleur = m.color || NAVY;
    // Rail étroit : icônes seules, les pages passent par le tiroir.
    const entrees = etroit ? [] : entreesModule(m);
    const pages = entrees.some((e) => e.route !== routePortail(m));
    const id = "cq-sommaire-" + m.key;
    return (
      <div key={m.key} className="cq-rail-module">
        <div className="cq-rail-ligne" data-pages={pages ? "1" : undefined}>
          {item(m.key, m.label, m.icon || "layout-grid", couleur, routePortail(m), "rail-nav-" + m.key, compteModule(m), !!m.reserveAdmin)}
          {pages && (
            <button type="button" className="cq-rail-deplier" style={teinte(couleur)} aria-controls={id}
              aria-expanded={ouvert === m.key} aria-label={"Pages de « " + m.label + " »"} onClick={(e) => basculer(m.key, e)}>
              <Icone name="chevron-right" size={16} />
            </button>
          )}
        </div>
        {pages && <Sommaire id={id} module={m} entrees={entrees} view={pageServie(view)} ouvert={ouvert === m.key}
          onNav={onNav} intention={intention} />}
      </div>
    );
  };

  return (
    <nav ref={rail} className="cq-rail" aria-label="Navigation principale" data-etroit={etroit ? "1" : undefined}
      data-deborde={etroit && deborde ? "1" : undefined} data-instantane={sec ? "1" : undefined}>
      <div className="cq-rail-liste" ref={liste}>
        {item("ma-semaine", "Ma semaine", "home", NAVY, "ma-semaine", "rail-nav-ma-semaine")}
        {vis(personnel).map(ligne)}
        <p className="cq-rail-titre" aria-hidden="true">Modules</p>
        {vis(modules).map(ligne)}
        {administration.length > 0 && <div className="cq-rail-filet" aria-hidden="true" />}
        {administration.map(ligne)}
      </div>
      {session && (
        <button type="button" className="cq-rail-moi" onClick={onMoi} aria-label="Moi : profil, messagerie et préférences"
          data-bulle="Moi" title={infobulle("Moi")} aria-current={view === "mon-profil" ? "page" : undefined}>
          <span className="cq-avatar" aria-hidden="true">{initiales || <Icone name="user" size={18} />}</span>
          <span className="cq-rail-moi-texte">
            <span className="cq-rail-moi-nom">{[session.prenom, session.nom].filter(Boolean).join(" ")}</span>
            {session.mutuelle && <span className="cq-rail-moi-role">{session.mutuelle}</span>}
          </span>
        </button>
      )}
    </nav>
  );
}
